import { Button, Chip } from "@heroui/react";
import { Snippet } from "@heroui/snippet";
import React, { Key, useCallback } from "react";
import AnimalModal from "../../models/Animal.modal.tsx";

import { AuthStore } from "@/store/useAuthStore.ts";
import { calculateAge } from "@/lib/utils.ts";
import { FormValues } from "@/models/Sila.Animals.schema.ts";
import { useDeleteSilaAnimal } from "@/features/Admin/Admin.api.ts";
import { toast } from "sonner";
import { NotebookTabs } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog.tsx";

interface SilaAnimals extends FormValues {
  _id: string;
}

interface RenderCellProps {
  data: SilaAnimals;
  columnKey: Key;
  setViewProfile: any;
}

const RenderCellSilaAnimals: React.FC<RenderCellProps> = ({
  data,
  columnKey,
}) => {
  const { farmInfo } = AuthStore();
  const { mutateAsync } = useDeleteSilaAnimal(data._id);

  function handleDeleteAnimal() {
    toast.promise(mutateAsync(data._id), {
      loading: "Deleting animal...",
      success: "Animal deleted successfully!",
      error: "Failed to delete animal.",
    });
  }

  const renderCell = useCallback(
    (data: SilaAnimals, columnKey: Key) => {
      const cellValue = data[columnKey as keyof SilaAnimals];

      switch (columnKey) {
        case "name":
          return <AnimalModal data={data} />;
        case "birthDate": {
          if (!data.birthDate) {
            return (
              <div className="flex flex-col">
                <p className="text-bold text-small capitalize">N/A</p>
              </div>
            );
          }
          const age = calculateAge(new Date(data.birthDate));

          return (
            <div className="flex flex-col">
              <p className="text-bold text-small capitalize">
                {data.birthDate}
              </p>
              <p className="text-bold text-tiny capitalize text-default-400">
                Age:{age.months} mon{" "}
              </p>
            </div>
          );
        }
        case "phases":
          return (
            <div className="flex flex-col">
              <div className="text-bold text-small capitalize">
                {((data.phases?.length ?? 0) === 0 && (
                  <Chip color="success" variant="flat">
                    Initial
                  </Chip>
                )) ||
                  ((data.phases?.length ?? 0) === 1 && (
                    <Chip color="primary" variant="flat">
                      Maternal Phase
                    </Chip>
                  )) ||
                  ((data.phases?.length ?? 0) === 2 && (
                    <Chip color="primary" variant="flat">
                      Wean Phase
                    </Chip>
                  )) ||
                  ((data.phases?.length ?? 0) === 3 && (
                    <Chip color="primary" variant="flat">
                      Yearning Phase
                    </Chip>
                  ))}
              </div>
            </div>
          );

        case "farmer":
          return (
            <div className="flex flex-col">
              <p className="text-bold text-small capitalize">
                {farmInfo.farmName}
              </p>
              <p className="text-bold text-tiny capitalize text-default-400">
                {farmInfo.id}
              </p>
            </div>
          );
        case "country":
          return (
            <div className="flex flex-col w-[100px]">
              <p>{farmInfo.city}</p>
            </div>
          );

        case "animalStatus":
          return (
            <Chip
              className="capitalize"
              color={
                data.animalStatus === "unknown"
                  ? "danger"
                  : data.animalStatus === "active"
                    ? "primary"
                    : data.animalStatus === "inactive"
                      ? "warning"
                      : data.animalStatus === "semen" ||
                          data.animalStatus === "embryo"
                        ? "success"
                        : undefined
              }
              size="sm"
              variant="flat"
            >
              {data.animalStatus}
            </Chip>
          );

        case "assignedSuperisor":
          return (
            <div>
              <Snippet color={"secondary"} size="sm" symbol="#" variant="flat">
                {data.assignedSupervisor}
              </Snippet>
            </div>
          );
        case "farmID":
          return <div />;

        case "actions":
          return (
            <div className={"flex flex-row gap-2"}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant={"faded"}
                    size={"sm"}
                    className="w-8 h-8 bg-green-200 border-none text-gray-700 hover:bg-green-300"
                  >
                    Actions
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end">
                  <DropdownMenuLabel>Actions</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuItem
                      asChild
                      className="hover:!bg-green-100 focus:!bg-green-200 cursor-pointer"
                    >
                      <a
                        href={`/admin/sila-owned-animals/updating/${data._id}`}
                      >
                        <NotebookTabs className="mr-2 h-4 w-4" />
                        <span>Update Animal</span>
                      </a>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant={"flat"} size={"sm"} color={"danger"}>
                    Delete
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[425px]">
                  <DialogHeader>
                    <DialogTitle>Delete Animal</DialogTitle>
                    <DialogDescription>
                      <p className={"font-normal text-lg"}>
                        Are You Sure, You wanted to Delete Animal?
                      </p>
                      <span>
                        <b>Animal Name:</b> {data.name}
                      </span>
                      <span>
                        <b>DGR:</b> {data.dgr}
                      </span>
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4"></div>
                  <DialogFooter>
                    <DialogClose asChild></DialogClose>
                    <Button
                      type="submit"
                      onPress={handleDeleteAnimal}
                      color="danger"
                      variant="light"
                    >
                      Delete changes
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          );
        default:
          if (typeof cellValue === "object") {
            return JSON.stringify(cellValue);
          } else {
            return cellValue;
          }
      }
    },
    [farmInfo.city, farmInfo.farmName, farmInfo.id, data],
  );

  return renderCell(data, columnKey);
};

export default RenderCellSilaAnimals;
